import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';

const initialState = {
  forms: [],
  formDetail: null,
  loading: false,
  saving: false,
  error: null,
};

const slice = createSlice({
  name: 'consentForm',
  initialState,
  reducers: {
    setLoading(state, action) { state.loading = action.payload; },
    setSaving(state, action) { state.saving = action.payload; },
    setForms(state, action) { state.forms = action.payload; },
    setFormDetail(state, action) { state.formDetail = action.payload; },
    setError(state, action) { state.error = action.payload; },
  },
});

export const { setLoading, setSaving, setForms, setFormDetail, setError } = slice.actions;
export default slice.reducer;

// ─── Thunks ─────────────────────────────────────────────────────────────────

export function getConsentForms(practiceId, callback) {
  return async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const res = await callApi(`/api/v1/practices/${practiceId}/forms/`, 'get');
      if (res.ok) {
        dispatch(setForms(res.data?.results || res.data || []));
        callback?.({ success: true, data: res.data });
      } else {
        dispatch(setError(res.data?.detail || 'Failed to load consent forms'));
        callback?.({ success: false });
      }
    } finally {
      dispatch(setLoading(false));
    }
  };
}

export function getConsentFormDetail(practiceId, formId, callback) {
  return async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const res = await callApi(`/api/v1/practices/${practiceId}/forms/${formId}/`, 'get');
      if (res.ok) {
        dispatch(setFormDetail(res.data));
        callback?.({ success: true, data: res.data });
      } else {
        callback?.({ success: false });
      }
    } finally {
      dispatch(setLoading(false));
    }
  };
}

export function createConsentForm(practiceId, payload, callback) {
  return async (dispatch) => {
    dispatch(setSaving(true));
    try {
      const res = await callApi(`/api/v1/practices/${practiceId}/forms/`, 'post', payload);
      if (res.ok) {
        callback?.({ success: true, data: res.data });
      } else {
        const msg = res.data?.detail || res.data?.message || 'Failed to create consent form';
        callback?.({ success: false, message: msg });
      }
    } finally {
      dispatch(setSaving(false));
    }
  };
}

export function updateConsentForm(practiceId, formId, payload, callback) {
  return async (dispatch) => {
    dispatch(setSaving(true));
    try {
      const res = await callApi(`/api/v1/practices/${practiceId}/forms/${formId}/`, 'patch', payload);
      if (res.ok) {
        callback?.({ success: true, data: res.data });
      } else {
        const msg = res.data?.detail || res.data?.message || 'Failed to update consent form';
        callback?.({ success: false, message: msg });
      }
    } finally {
      dispatch(setSaving(false));
    }
  };
}

export function deleteConsentForm(practiceId, formId, callback) {
  return async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const res = await callApi(`/api/v1/practices/${practiceId}/forms/${formId}/`, 'delete');
      if (res.ok) {
        callback?.({ success: true });
      } else {
        const msg = res.data?.detail || 'Failed to delete consent form';
        dispatch(setError(msg));
        callback?.({ success: false, message: msg });
      }
    } finally {
      dispatch(setLoading(false));
    }
  };
}
